
import { Grid3X3, List, Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { TooltipProvider } from "@/components/ui/tooltip";
import { NetworkIcon } from "../icons/NetworkIcon";
import FrameIcon from "../icons/FrameIcon";
import FreeformIcon from "../icons/FreeformIcon";
import Icon from "@/components/ui/icon";
import { useEffect } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useSidebar } from "@/components/ui/sidebar";
import { Trash2, ArrowLeft } from "lucide-react";
import ArchiveIcon from "@/components/icons/ArchiveIcon";
import { useEditableContent } from "@/hooks/useEditableContent";
import { normalizeDescription } from "@/utils/contentUtils";
import EditableText from "./EditableText";
import { useNavigate, Link } from "react-router-dom";
import { ViewMode } from "@/hooks/usePersistentViewMode";

interface ContentHeaderProps {
  contentItem: any;
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  onEditClick: () => void;
  onArchiveClick: () => void;
  onDeleteClick: () => void;
  refreshButton?: React.ReactNode;
  refetchContent?: () => void;
}

const ContentHeader = ({ 
  contentItem,
  viewMode, 
  onViewModeChange,
  onArchiveClick,
  onDeleteClick,
  refreshButton,
  refetchContent
}: ContentHeaderProps) => {
  const isMobile = useIsMobile();
  const { openMobile, setOpenMobile } = useSidebar();
  const navigate = useNavigate();
  
  const {
    title,
    description,
    setTitle,
    setDescription,
    saveTitle,
    saveDescription,
    isLoading
  } = useEditableContent({ 
    contentItem: {
      id: contentItem.id,
      title: contentItem.title,
      description: normalizeDescription(contentItem.description),
    },
    refetchContent 
  });

  const handleTitleChange = (newTitle: string) => {
    setTitle(newTitle);
  };

  const handleDescriptionChange = (newDescription: string) => {
    setDescription(newDescription);
  };
  
  useEffect(() => {
    console.log("ContentHeader rendered with:", {
      title: title || "[EMPTY TITLE]",
      description: description || "[EMPTY DESCRIPTION]",
      viewMode,
      isMobile,
      openMobile
    });
  }, [title, description, viewMode, isMobile, openMobile]);

  const handleMenuClick = () => {
    console.log("Menu button clicked, current openMobile state:", openMobile);
    setOpenMobile(true);
  };

  const handleBackClick = () => {
    navigate(-1);
  };

  return (
    <div 
      className={cn(
        "bg-background sticky top-0",
        isMobile ? "flex flex-col pb-4 px-7 space-y-3" : "flex items-center justify-between px-7"
      )} 
      style={isMobile ? 
        { zIndex: 20 } : 
        { 
          height: '80px',
          maxHeight: '80px',
          minHeight: '80px',
          paddingTop: '0'
        }
      }
    >
      <div className={cn(
        "flex",
        isMobile ? "flex-row items-center justify-between w-full pt-2" : "flex-col space-y-[-4px]"
      )}>
        <div className="flex items-center">
          {isMobile && (
            <Link to="/dashboard">
              <img 
                src="/lovable-uploads/538dfb2b-93c1-451f-9a81-7393dd3964c9.png" 
                alt="Clustr Icon" 
                className="h-[54px] mr-5" 
                style={{ height: '54px', width: 'auto' }}
              />
            </Link>
          )}
          
          <div className="flex flex-col">
            <EditableText
              value={title}
              onChange={handleTitleChange}
              onSave={saveTitle}
              isHeading
              isLoading={isLoading}
            />
            
            <EditableText
              value={description}
              onChange={handleDescriptionChange}
              onSave={saveDescription}
              isMultiline
              placeholder="Add a description..."
              isLoading={isLoading}
            />
          </div>
        </div>
        
        {isMobile && (
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={handleBackClick}
            className="h-10 w-10 rounded-full hover:bg-muted"
          >
            <Icon 
              icon={ArrowLeft} 
              size={16} 
              className="text-muted-foreground"
            />
          </Button>
        )}
      </div>
      
      {isMobile ? (
        <div className="flex items-center justify-between w-full">
          <Button 
            variant="ghost" 
            size="icon" 
            className="h-10 w-10 bg-muted rounded-xl"
            onClick={handleMenuClick}
          >
            <Icon 
              icon={Menu} 
              size={20} 
              strokeWidth={2}
              className="text-muted-foreground"
            />
          </Button>
          
          <div className="flex items-center space-x-2">
            <Button 
              variant="outline" 
              size="icon" 
              onClick={onArchiveClick}
              className="w-10 h-10 p-0 rounded-xl bg-muted hover:bg-accent border-border"
            >
              <ArchiveIcon size={16} className="text-muted-foreground" />
            </Button>
            
            <Button 
              variant="outline" 
              size="icon" 
              onClick={onDeleteClick}
              className="w-10 h-10 p-0 rounded-xl bg-muted hover:bg-accent border-border text-red-500 hover:text-red-600"
            >
              <Icon icon={Trash2} size={16} />
            </Button>
            
            {refreshButton && refreshButton}
            
            <TooltipProvider>
              <div className="bg-muted p-1 rounded-lg flex items-center">
                <ToggleGroup type="single" value={viewMode} onValueChange={(value) => value && onViewModeChange(value as ViewMode)}>
                  <ToggleGroupItem value="freeform" className={cn("rounded-md h-10 w-10 flex items-center justify-center", {
                    "!bg-background dark:!bg-background shadow-sm": viewMode === "freeform",
                  })}>
                    <FreeformIcon 
                      size={20} 
                      strokeWidth={2}
                      className={viewMode === "freeform" ? "text-foreground" : "text-muted-foreground"}
                    />
                  </ToggleGroupItem>
                  <ToggleGroupItem value="frame" className={cn("rounded-md h-10 w-10 flex items-center justify-center", {
                    "!bg-background dark:!bg-background shadow-sm": viewMode === "frame",
                  })}>
                    <FrameIcon
                      size={20}
                      strokeWidth={2}
                      className={viewMode === "frame" ? "text-foreground" : "text-muted-foreground"}
                    />
                  </ToggleGroupItem>
                  <ToggleGroupItem value="grid" className={cn("rounded-md h-10 w-10 flex items-center justify-center", {
                    "!bg-background dark:!bg-background shadow-sm": viewMode === "grid",
                  })}>
                    <Icon 
                      icon={Grid3X3}
                      size={20}
                      strokeWidth={2}
                      className={viewMode === "grid" ? "text-foreground" : "text-muted-foreground"}
                    />
                  </ToggleGroupItem>
                  <ToggleGroupItem value="list" className={cn("rounded-md h-10 w-10 flex items-center justify-center", {
                    "!bg-background dark:!bg-background shadow-sm": viewMode === "list",
                  })}>
                    <Icon 
                      icon={List}
                      size={20}
                      strokeWidth={2}
                      className={viewMode === "list" ? "text-foreground" : "text-muted-foreground"}
                    />
                  </ToggleGroupItem>
                  <ToggleGroupItem value="network" className={cn("rounded-md h-10 w-10 flex items-center justify-center", {
                    "!bg-background dark:!bg-background shadow-sm": viewMode === "network",
                  })}>
                    <NetworkIcon 
                      size={20} 
                      strokeWidth={2}
                      className={viewMode === "network" ? "text-foreground" : "text-muted-foreground"}
                    />
                  </ToggleGroupItem>
                </ToggleGroup>
              </div>
            </TooltipProvider>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-4">
          <div className="flex items-center space-x-2">
            <Button 
              variant="outline" 
              size="icon" 
              onClick={onArchiveClick}
              className="w-10 h-10 p-0 rounded-xl bg-muted hover:bg-accent border-border"
            >
              <ArchiveIcon size={16} className="text-muted-foreground" />
            </Button>
            
            <Button 
              variant="outline" 
              size="icon" 
              onClick={onDeleteClick}
              className="w-10 h-10 p-0 rounded-xl bg-muted hover:bg-accent border-border text-red-500 hover:text-red-600"
            >
              <Icon icon={Trash2} size={16} />
            </Button>
            
            {refreshButton && (
              <div className="ml-2">
                {refreshButton}
              </div>
            )}
          </div>
          
          <div>
            <TooltipProvider>
              <div className="bg-muted p-1 rounded-lg flex items-center">
                <ToggleGroup type="single" value={viewMode} onValueChange={(value) => value && onViewModeChange(value as ViewMode)}>
                  <ToggleGroupItem value="freeform" className={cn("rounded-md h-10 w-10 flex items-center justify-center", {
                    "!bg-background dark:!bg-background shadow-sm": viewMode === "freeform",
                  })}>
                    <FreeformIcon 
                      size={20} 
                      strokeWidth={2}
                      className={viewMode === "freeform" ? "text-foreground" : "text-muted-foreground"}
                    />
                  </ToggleGroupItem>
                  <ToggleGroupItem value="frame" className={cn("rounded-md h-10 w-10 flex items-center justify-center", {
                    "!bg-background dark:!bg-background shadow-sm": viewMode === "frame",
                  })}>
                    <FrameIcon
                      size={20}
                      strokeWidth={2}
                      className={viewMode === "frame" ? "text-foreground" : "text-muted-foreground"}
                    />
                  </ToggleGroupItem>
                  <ToggleGroupItem value="grid" className={cn("rounded-md h-10 w-10 flex items-center justify-center", {
                    "!bg-background dark:!bg-background shadow-sm": viewMode === "grid",
                  })}>
                    <Icon 
                      icon={Grid3X3}
                      size={20}
                      strokeWidth={2}
                      className={viewMode === "grid" ? "text-foreground" : "text-muted-foreground"}
                    />
                  </ToggleGroupItem>
                  <ToggleGroupItem value="list" className={cn("rounded-md h-10 w-10 flex items-center justify-center", {
                    "!bg-background dark:!bg-background shadow-sm": viewMode === "list",
                  })}>
                    <Icon 
                      icon={List}
                      size={20}
                      strokeWidth={2}
                      className={viewMode === "list" ? "text-foreground" : "text-muted-foreground"}
                    />
                  </ToggleGroupItem>
                  <ToggleGroupItem value="network" className={cn("rounded-md h-10 w-10 flex items-center justify-center", {
                    "!bg-background dark:!bg-background shadow-sm": viewMode === "network",
                  })}>
                    <NetworkIcon 
                      size={20} 
                      strokeWidth={2}
                      className={viewMode === "network" ? "text-foreground" : "text-muted-foreground"}
                    />
                  </ToggleGroupItem>
                </ToggleGroup>
              </div>
            </TooltipProvider>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContentHeader;
